/**
 * IV Rank: where current IV sits relative to the historical range.
 * Formula: (currentIV - min) / (max - min) * 100
 * @param {number} currentIV - as percentage (e.g. 18.5)
 * @param {number[]} historicalIVs - array of IV percentages
 * @returns {number|null}
 */
export function calculateIVRank(currentIV, historicalIVs) {
  if (!historicalIVs || historicalIVs.length === 0) return null;
  const min = Math.min(...historicalIVs);
  const max = Math.max(...historicalIVs);
  if (max === min) return 50;
  return Math.round(((currentIV - min) / (max - min)) * 100);
}

/**
 * IV Percentile: what percentage of historical readings are below current.
 * @param {number} currentIV - as percentage (e.g. 18.5)
 * @param {number[]} historicalIVs - array of IV percentages
 * @returns {number|null}
 */
export function calculateIVPercentile(currentIV, historicalIVs) {
  if (!historicalIVs || historicalIVs.length === 0) return null;
  const below = historicalIVs.filter((iv) => iv < currentIV).length;
  return Math.round((below / historicalIVs.length) * 100);
}

/**
 * Interpret IV rank/percentile for the user.
 */
export function getInterpretation(ivRank, ivPercentile) {
  if (ivRank === null || ivPercentile === null) {
    return { text: 'Insufficient data', color: 'text-gray-500', recommendation: '' };
  }

  if (ivPercentile > 70) {
    return {
      text: 'IV is elevated',
      color: 'text-orange-600',
      recommendation: 'Good for selling premium (short straddle/strangle, iron condors)',
    };
  }
  if (ivPercentile < 30) {
    return {
      text: 'IV is low',
      color: 'text-green-600',
      recommendation: 'Good for buying options (long straddle/strangle, debit spreads)',
    };
  }
  return {
    text: 'IV is neutral',
    color: 'text-blue-600',
    recommendation: 'No strong edge from volatility — trade directionally or stay patient',
  };
}
